import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Position } from '../types/position.ts';

export type PositionEditingState = {
  // Core selection (single source of truth)
  startIndex: number | null;
  endIndex: number | null;

  // Popup flow: click "Edit" on a marker (by external id)
  // Requires getIndexById prop on the Provider
  setFromPositionPopup: (id: number | string) => void;

  // Mutators by external id / time / index
  setStartById: (id: number | string) => void;
  setEndById: (id: number | string) => void;
  setStartByIndex: (index: number) => void;
  setEndByIndex: (index: number) => void;
  setSelectionRange: (startIndex: number, endIndex: number) => void;
  resetPopupSelectionTarget: () => void;

  setStartByTime: (iso: string) => void;
  setEndByTime: (iso: string) => void;

  // Compute end from a count (>=1) relative to start
  setCountFromStart: (count: number) => void;

  // Reset selection and close
  clear: () => void;
};

const PositionEditingContext = createContext<PositionEditingState | null>(null);

export const usePositionEditing = () => {
  const ctx = useContext(PositionEditingContext);
  if (!ctx) throw new Error('usePositionEditing must be used within PositionEditingProvider');
  return ctx;
};

// ----- Binary search: first index with time >= target (or last if none) -----
function nextIndexByTime(positions: Position[], iso: string): number {
  const target = new Date(iso).getTime();
  let lo = 0, hi = positions.length; // search in [lo, hi)
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    const tMid = new Date(positions[mid].time).getTime();
    if (tMid < target) lo = mid + 1; else hi = mid;
  }
  // lo is the first index with time >= target, or positions.length if none
  return Math.min(Math.max(lo, 0), Math.max(positions.length - 1, 0));
}

function previousIndexByTime(positions: Position[], iso: string): number {
  const target = new Date(iso).getTime();
  let lo = 0, hi = positions.length; // search in [lo, hi)
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    const tMid = new Date(positions[mid].time).getTime();
    if (tMid <= target) lo = mid + 1; else hi = mid;
  }
  const idx = lo - 1;
  return Math.min(Math.max(idx, 0), Math.max(positions.length - 1, 0));
}

export const PositionEditingProvider: React.FC<{
  positions: Position[];
  resetSelectionKey?: number;
  children: React.ReactNode;
}> = ({ positions, resetSelectionKey = 0, children }) => {
  const [startIndex, setStartIndex] = useState<number | null>(null);
  const [endIndex, setEndIndex] = useState<number | null>(null);
  const [nextPopupTarget, setNextPopupTarget] = useState<'start' | 'end'>('start');

  const getIndexById = React.useCallback(
    (id: number | string) => positions.findIndex(p => p.id === id),
    [positions]
  );

  const normalizeIndex = React.useCallback((index: number) => {
    const idx = Number.isFinite(index) ? Math.floor(index) : -1;
    if (idx < 0) return -1;
    if (idx >= positions.length) return positions.length - 1;
    return idx;
  }, [positions.length]);

  const setSelectionRangeInternal = React.useCallback((start: number, end: number) => {
    if (!positions.length) return;
    const a = normalizeIndex(start);
    const b = normalizeIndex(end);
    if (a < 0 || b < 0) return;
    const [low, high] = a <= b ? [a, b] : [b, a];
    setStartIndex(low);
    setEndIndex(high);
  }, [normalizeIndex, positions.length]);

  useEffect(() => {
    setStartIndex(null);
    setEndIndex(null);
    setNextPopupTarget('start');
  }, [resetSelectionKey]);

  const value = useMemo<PositionEditingState>(() => ({
    startIndex,
    endIndex,

    setFromPositionPopup: (id) => {
      const idx = getIndexById(id);
      if (idx == null || idx < 0 || idx >= positions.length) return;

      const firstId = positions.length > 0 ? positions[0].id : null;
      const lastId = positions.length > 0 ? positions[positions.length - 1].id : null;
      const currentStartId = startIndex != null && positions[startIndex] ? positions[startIndex].id : null;
      const currentEndId = endIndex != null && positions[endIndex] ? positions[endIndex].id : null;

      if (startIndex == null || endIndex == null) {
        if (startIndex == null && endIndex == null) {
          setSelectionRangeInternal(idx, idx);
        } else if (startIndex == null) {
          setSelectionRangeInternal(idx, endIndex ?? idx);
        } else {
          setSelectionRangeInternal(startIndex, idx);
        }
        setNextPopupTarget('end');
        return;
      }

      const isStartDefault = currentStartId === firstId;
      const isEndDefault = currentEndId === lastId;

      if (isStartDefault && isEndDefault) {
        setSelectionRangeInternal(idx, endIndex);
        setNextPopupTarget('end');
      } else if (!isStartDefault && !isEndDefault) {
        setSelectionRangeInternal(idx, positions.length - 1);
        setNextPopupTarget('end');
      } else if (!isStartDefault) {
        setSelectionRangeInternal(startIndex, idx);
        setNextPopupTarget('start');
      } else {
        setSelectionRangeInternal(idx, endIndex);
        setNextPopupTarget('end');
      }
    },

    setStartById: (id) => {
      const i = getIndexById(id);
      if (i != null && i >= 0 && i < positions.length) {
        setSelectionRangeInternal(i, endIndex ?? i);
      }
    },
    setEndById: (id) => {
      const i = getIndexById(id);
      if (i != null && i >= 0 && i < positions.length) {
        setSelectionRangeInternal(startIndex ?? i, i);
      }
    },
    setStartByIndex: (index) => {
      const i = normalizeIndex(index);
      if (i >= 0) {
        setSelectionRangeInternal(i, endIndex ?? i);
        setNextPopupTarget('end');
      }
    },
    setEndByIndex: (index) => {
      const i = normalizeIndex(index);
      if (i >= 0) {
        setSelectionRangeInternal(startIndex ?? i, i);
        setNextPopupTarget('end');
      }
    },
    setSelectionRange: (start, end) => {
      setSelectionRangeInternal(start, end);
    },
    resetPopupSelectionTarget: () => {
      setNextPopupTarget('start');
    },

    setStartByTime: (iso) => {
      if (!positions.length) return;
      const i = nextIndexByTime(positions, iso);
      setSelectionRangeInternal(i, endIndex ?? i);
      setNextPopupTarget('end');
    },
    setEndByTime: (iso) => {
      if (!positions.length) return;
      const i = previousIndexByTime(positions, iso);
      setSelectionRangeInternal(startIndex ?? i, i);
      setNextPopupTarget('end');
    },

    setCountFromStart: (count) => {
      if (startIndex == null || !Number.isFinite(count) || count <= 0 || !positions.length) return;
      const c = Math.floor(count);
      const maxIdx = positions.length - 1;
      const target = Math.min(maxIdx, startIndex + (c - 1));
      setEndIndex(target);
    },

    clear: () => {
      setStartIndex(null);
      setEndIndex(null);
    }
  }), [startIndex, endIndex, positions, nextPopupTarget]);

  return <PositionEditingContext.Provider value={value}>{children}</PositionEditingContext.Provider>;
};
